import axios, { CancelTokenSource } from 'axios';
import React, { useEffect, useState } from 'react';
import { Error, newUuid } from '../lib/constants';

interface IUseGetProps {
  response: [
    object | object[] | object[][] | null,
    React.Dispatch<React.SetStateAction<object | object[] | object[][] | null>>,
    number | null
  ];
  update: [
    boolean,
    React.Dispatch<React.SetStateAction<string | boolean>>,
    () => void
  ];
  page: [
    number,
    React.Dispatch<React.SetStateAction<number>>,
    number,
    React.Dispatch<React.SetStateAction<number>>
  ];
  search: [string, React.Dispatch<React.SetStateAction<string>>];
  url: [string, React.Dispatch<React.SetStateAction<string>>];
  params: [string, React.Dispatch<React.SetStateAction<object>>];
}
type UseGetProps = IUseGetProps;

export const useGet = (props: {
  apiUrl?: string;
  rootUrl?: string;
  urlParams?: object | null;
  isPagination?: boolean | false;
  paginationLimit?: number | 15;
  isSearch?: boolean | false;
  search?: string | '';
  callbackResponse?: (response: object | object[] | object[][] | null) => void;
  callbackError?: (error: Error) => void;
}): UseGetProps => {
  const [response, setResponse] = useState<
    object | object[] | object[][] | null
  >(null);
  const [listCount, setListCount] = useState<number | null>(null);
  const [isUpdateList, setIsUpdateList] = useState<string | boolean>(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLimit, setPageLimit] = useState(props.paginationLimit);
  const [searchQuery, setSearchQuery] = useState(props.search);
  const [isRequest, setIsRequest] = useState(false);
  const [cancelTokenSource, setCancelTokenSource] =
    useState<CancelTokenSource | null>(null);

  const [apiUrl, setApiUrl] = useState(props.apiUrl);
  const [apiUrlParams, setApiUrlParams] = useState(props.urlParams);

  useEffect(() => {
    return () => {
      setResponse(null);
      setListCount(null);
      setSearchQuery('');
      setApiUrlParams(null);
      setIsRequest(false);
      if (cancelTokenSource) {
        cancelTokenSource.cancel();
      }
    };
  }, []);

  const escapeRegExp = (string: string): string => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  };

  const replaceAll = (str: string, find: string, replace: string): string => {
    return str.replace(new RegExp(escapeRegExp(find), 'g'), replace);
  };

  const getApiUrlParams = (): string => {
    const params: Record<string, string> = {
      ...((!!apiUrlParams && apiUrlParams) || {}),
      ...((props.isSearch && { query: searchQuery || '' }) || {}),
      ...((props.isPagination && {
        page: pageNumber,
        limit: pageLimit
      }) ||
        {})
    } as Record<string, string>;

    return replaceAll(new URLSearchParams(params).toString(), '%2C', ',');
  };

  const getApiUrl = (): string => {
    return (
      apiUrl + (getApiUrlParams().length > 0 ? '?' + getApiUrlParams() : '')
    );
  };

  useEffect(() => {
    // TODO Доработать механизм залипания запроса.
    // console.log('list: ' + isUpdateList.toString())
    if (isRequest || cancelTokenSource) {
      return;
    }
    setIsRequest(true);

    const source = axios.CancelToken.source();
    setCancelTokenSource(source);

    void axios
      .get(getApiUrl(), {
        withCredentials: false,
        cancelToken: source.token,
        baseURL: props.rootUrl,
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then(response => {
        setIsRequest(false);
        setCancelTokenSource(null);
        if (props.callbackResponse) {
          props.callbackResponse(
            ((!!response && !!response.data && response.data) || []) as
              | object
              | object[]
              | object[][]
              | null
          );
        }
        const count = props.isPagination
          ? Number(response.headers['total-records'])
          : (!!response &&
              !!response.data &&
              Array.isArray(response.data) &&
              (response.data as object[] | object[][]).length) ||
            0;
        setListCount(count);
        const data = ((!!response && !!response.data && response.data) ||
          null) as object | object[] | object[][] | null;
        setResponse(data);
      })
      .catch((error: Error) => {
        setIsRequest(false);
        setCancelTokenSource(null);
        if (props.callbackError) {
          props.callbackError(error);
        }
      });
  }, [isUpdateList, apiUrl, apiUrlParams, pageNumber, pageLimit, searchQuery]);

  const changeUpdateList = (): void => {
    const uuid = newUuid();
    // console.log('action: ' + uuid)
    setIsUpdateList(uuid);
  };

  return <IUseGetProps>{
    response: [(!!response && response) || null, setResponse, listCount] as [
      object | object[] | object[][] | null,
      React.Dispatch<
        React.SetStateAction<object | object[] | object[][] | null>
      >,
      number | null
    ],
    update: [isUpdateList as boolean, setIsUpdateList, changeUpdateList],
    page: [pageNumber, setPageNumber, pageLimit, setPageLimit],
    search: [searchQuery, setSearchQuery],
    url: [apiUrl, setApiUrl],
    params: [getApiUrlParams(), setApiUrlParams]
  };
};
